/**
 * Loads the built-in Mixamo avatars (Draco + KTX2 GLBs), the shared animation clips and
 * optional VRM avatars by URL. Loaded assets are cached and cloned for reuse.
 */
import { ArkitFace } from '../avatars/face.ts';
import { LIP_LINE, addJaw, type Jaw } from '../avatars/jaw.ts';
import * as THREE from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { VRMLoaderPlugin, VRMUtils, type VRM } from '@pixiv/three-vrm';
import { BASE_PATH } from '../lib/api.ts';
import type { AvatarId } from '../app/profile.ts';

export interface LoadedAvatar {
  root: THREE.Group;
  /** Standing height in metres after normalisation. */
  height: number;
  mixer?: THREE.AnimationMixer;
  vrm?: VRM;
  /** The mouth: a jaw added to the built-in characters, which have no face rig. */
  jaw?: Jaw;
  /** A face with ARKit shapes (Luma): lip sync drives it. */
  face?: ArkitFace;
}

export class AvatarLoader {
  private readonly gltf: GLTFLoader;
  private readonly cache = new Map<string, Promise<GLTF>>();
  private clips: Promise<THREE.AnimationClip[]> | undefined;

  constructor(renderer: THREE.WebGLRenderer) {
    const draco = new DRACOLoader().setDecoderPath(`${BASE_PATH}libs/draco/`);
    const ktx2 = new KTX2Loader()
      .setTranscoderPath(`${BASE_PATH}libs/basis/`)
      .detectSupport(renderer);
    this.gltf = new GLTFLoader()
      .setDRACOLoader(draco)
      .setKTX2Loader(ktx2)
      // Luma's body is meshopt-compressed: Draco cannot carry her face shapes.
      .setMeshoptDecoder(MeshoptDecoder);
    this.gltf.register((parser) => new VRMLoaderPlugin(parser));
  }

  private load(url: string): Promise<GLTF> {
    if (!this.cache.has(url)) {
      this.cache.set(
        url,
        this.gltf.loadAsync(url).catch((err) => {
          this.cache.delete(url);
          throw err;
        }),
      );
    }
    return this.cache.get(url)!;
  }

  loadAnimations(): Promise<THREE.AnimationClip[]> {
    this.clips ??= this.load(`${BASE_PATH}assets/animations/animations.glb`).then(
      (g) => g.animations,
    );
    return this.clips;
  }

  /**
   * Luma's body (2026-10-06): a MetaPerson export with ARKit face shapes, real teeth and eyes
   * (`pnpm assets:host`). It keeps its own size; the height is the top of the head bone (the
   * bounding box reaches 1.99 m). Her mouth opens with the `jawOpen` shape.
   */
  async loadHost(): Promise<LoadedAvatar> {
    const gltf = await this.load(HOST_URL);
    const root = cloneSkeleton(gltf.scene) as THREE.Group;
    normalise(root);
    root.updateMatrixWorld(true);
    const top = root.getObjectByName('HeadTop_End');
    const height = top ? top.getWorldPosition(new THREE.Vector3()).y : 1.75;
    tuneAvatarMaterials(root);
    tuneHostMaterials(root);
    const face = new ArkitFace(root);
    return { root, height, jaw: face.jaw(), face };
  }

  /** Fetches Luma's body and the clips ahead of use (the lobby, while the code is typed). */
  warmHost(): void {
    this.load(HOST_URL).catch(() => undefined);
    this.loadAnimations().catch(() => undefined);
  }

  /** Fetches and decodes a built-in avatar and the clips ahead of use (nothing is shown). */
  warm(id: AvatarId, lod: 'full' | 'lod1' = 'full'): void {
    const suffix = lod === 'lod1' ? '-lod1' : '';
    this.load(`${BASE_PATH}assets/avatars/${id}${suffix}.glb`).catch(() => undefined);
    this.loadAnimations().catch(() => undefined);
  }

  /** A fresh instance of a built-in avatar, normalised to stand on y=0 facing +Z. */
  async loadBuiltIn(
    id: AvatarId,
    options: { animation?: string; lod?: 'full' | 'lod1' } = {},
  ): Promise<LoadedAvatar> {
    const suffix = options.lod === 'lod1' ? '-lod1' : '';
    const gltf = await this.load(`${BASE_PATH}assets/avatars/${id}${suffix}.glb`);
    const root = cloneSkeleton(gltf.scene) as THREE.Group;
    // The jaw first: its weights live in geometry shared by every copy of this avatar, so a
    // second copy must have the jaw bone before anything (the bounds below) skins it.
    const jaw = addJaw(root, { lipY: LIP_LINE[id] });
    const height = normalise(root);
    tuneAvatarMaterials(root);
    const avatar: LoadedAvatar = { root, height, jaw };
    if (options.animation) {
      const clips = await this.loadAnimations();
      const clip = clips.find((c) => c.name === options.animation);
      if (clip) {
        avatar.mixer = new THREE.AnimationMixer(root);
        avatar.mixer.clipAction(clip).play();
      }
    }
    return avatar;
  }

  /**
   * A fresh rigged mannequin body (skinned, unposed, in the file's own units) for the ward
   * to pose and bake. The cached file is never mutated.
   */
  async loadMannequinBody(
    sex: 'male' | 'female',
    lod: 'lod0' | 'lod1' | 'lod2',
  ): Promise<THREE.Object3D> {
    const gltf = await this.load(`${BASE_PATH}assets/mannequins/mannequin-${sex}-${lod}.glb`);
    return cloneSkeleton(gltf.scene);
  }

  /** Loads a VRM from a URL (VRM 0 and 1). */
  async loadVrm(url: string): Promise<LoadedAvatar> {
    const gltf = await this.gltf.loadAsync(url);
    const vrm = (gltf.userData as { vrm?: VRM }).vrm;
    if (!vrm) throw new Error('Not a VRM file');
    VRMUtils.removeUnnecessaryVertices(gltf.scene);
    VRMUtils.combineSkeletons(gltf.scene);
    VRMUtils.rotateVRM0(vrm);
    const root = vrm.scene as THREE.Group;
    const height = normalise(root);
    return { root, height, vrm };
  }
}

/** Puts the feet on y=0, keeps real-world scale when the model is in metres, fixes cm models. */
/** Luma's body, made by `pnpm assets:host`. */
const HOST_URL = `${BASE_PATH}assets/host/luma.glb`;

/**
 * A Mixamo clip for Luma's MetaPerson skeleton (2026-10-06, checked in renders): the bones
 * share Mixamo's names without the prefix, and the clips carry positions and scales in
 * Mixamo's units, so only rotations are kept; the hips' rest rotation differs between the
 * two rigs (she lay on her back), so the hips stay at rest.
 */
export function retargetForHost(clip: THREE.AnimationClip): THREE.AnimationClip {
  const out = clip.clone();
  out.tracks = out.tracks
    .filter((t) => t.name.endsWith('.quaternion') && !/Hips\.quaternion$/.test(t.name))
    .map((t) => {
      t.name = t.name.replace(/^mixamorig:?/, '');
      return t;
    });
  return out;
}

/**
 * Luma's materials after the general tuning: her hair was blended transparency, which sorts
 * strands wrongly from some angles, so it becomes a cut-out with alpha to coverage (smooth
 * edges under the headset's multisampling); her eyes keep a little shine.
 */
function tuneHostMaterials(root: THREE.Object3D): void {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const m = mesh.material as THREE.MeshStandardMaterial;
    if (m.name === 'haircut') {
      m.transparent = false;
      m.alphaTest = 0.4;
      m.alphaToCoverage = true;
      m.depthWrite = true;
      m.side = THREE.DoubleSide;
    }
    if (/Eyeball/.test(m.name)) m.roughness = 0.25;
    m.needsUpdate = true;
  });
}

function normalise(root: THREE.Object3D): number {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  let height = box.max.y - box.min.y;
  if (height > 50) {
    // Centimetre model.
    root.scale.multiplyScalar(0.01);
    root.updateMatrixWorld(true);
    box.setFromObject(root);
    height = box.max.y - box.min.y;
  }
  root.position.y -= box.min.y;
  root.position.x -= (box.min.x + box.max.x) / 2;
  root.position.z -= (box.min.z + box.max.z) / 2;
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.frustumCulled = false;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
    }
  });
  return height;
}

/**
 * The Mixamo characters came through the FBX conversion with a metallic factor of 0.5 and a
 * specular extension, which renders cloth and skin as dark glossy plastic under the room's
 * lighting (there is no environment map to reflect). People are not metal: metalness off,
 * one even cloth-like roughness, and a soft specular.
 */
export function tuneAvatarMaterials(root: THREE.Object3D): void {
  const seen = new Set<THREE.Material>();
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      if (seen.has(m)) continue;
      seen.add(m);
      const std = m as THREE.MeshStandardMaterial & { specularColor?: THREE.Color };
      if (!('metalness' in std)) continue;
      std.metalness = 0;
      std.metalnessMap = null;
      // The roughness texture from the conversion is mostly glossy and multiplies the factor,
      // so it goes too: cloth and skin get one even, soft roughness.
      std.roughnessMap = null;
      std.roughness = std.transparent ? 0.65 : 0.82;
      const phys = std as THREE.MeshPhysicalMaterial;
      if (phys.isMeshPhysicalMaterial) {
        phys.specularIntensityMap = null;
        phys.specularColorMap = null;
        phys.specularIntensity = 0.35;
      }
      std.specularColor?.setScalar(1);
      std.needsUpdate = true;
    }
  });
}
